import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { Avatar } from '../components/Avatar';
import { StatusBadge } from '../components/StatusBadge';
import type { CaseStatus, CaseSummary } from '../types';

const FILTER_KEYS: (CaseStatus | 'all')[] = ['all', 'open', 'pending', 'closed'];

type ListResponse = { items: CaseSummary[]; nextCursor: string | null };

export function Cases({
  onOpen,
  searchQuery,
}: {
  onOpen?: (caseId: string) => void;
  searchQuery: string;
}) {
  const { t } = useTranslation(['cases', 'common']);
  const [cases, setCases] = useState<CaseSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CaseStatus | 'all'>('all');
  const requestId = useRef(0);

  useEffect(() => {
    const myReq = ++requestId.current;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (filter !== 'all') params.set('status', filter);
        const data = await apiFetch<ListResponse>(`/api/cases?${params}`);
        if (myReq !== requestId.current) return;
        setCases(data.items);
        setNextCursor(data.nextCursor);
      } catch (e) {
        if (myReq !== requestId.current) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (myReq === requestId.current) setLoading(false);
      }
    })();
  }, [filter]);

  // Local filter for free-text search (cases list is small enough that
  // server-side trigram search isn't needed yet — number/area/client
  // substring match is fine).
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return cases;
    return cases.filter(
      (c) =>
        c.caseNumber.toLowerCase().includes(q) ||
        (c.area ?? '').toLowerCase().includes(q) ||
        (c.client?.name ?? '').toLowerCase().includes(q),
    );
  }, [cases, searchQuery]);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      params.set('cursor', nextCursor);
      const data = await apiFetch<ListResponse>(`/api/cases?${params}`);
      setCases((prev) => [...prev, ...data.items]);
      setNextCursor(data.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingMore(false);
    }
  }

  if (loading && cases.length === 0) return <div className="empty">{t('list.loading')}</div>;
  if (error) return <div className="empty">{t('common:errors.with_message', { message: error })}</div>;

  return (
    <div>
      <div className="filter-row">
        {FILTER_KEYS.map((key) => (
          <div
            key={key}
            className={`type-btn ${filter === key ? 'active' : ''}`}
            onClick={() => setFilter(key)}
            style={{ flex: 'unset', padding: '6px 14px' }}
          >
            {t(`filters.${key}`)}
          </div>
        ))}
      </div>

      <div className="full-panel">
        <div className="panel-header">
          {filter === 'all' ? t('list.title_all') : t(`statuses.${filter}`)}
        </div>
        {filtered.length === 0 ? (
          <div className="empty">
            {searchQuery.trim() ? t('list.empty_search') : t('list.empty')}
          </div>
        ) : (
          filtered.map((c) => {
            const clientName = c.client?.name ?? '—';
            const sub = [c.caseNumber, c.area].filter(Boolean).join(' · ');
            const showPriority = c.priority === 'high' || c.priority === 'urgent';
            return (
              <div key={c.id} className="case-row" onClick={() => onOpen?.(c.id)}>
                <Avatar name={clientName} size="md" />
                <div className="case-info">
                  <div className="case-name">
                    {clientName}
                    {c.area ? ` — ${c.area}` : ''}
                  </div>
                  <div className="case-sub">{sub}</div>
                </div>
                {showPriority && (
                  <span className={`pill pill-prio-${c.priority}`}>
                    {t(`priorities.${c.priority}`)}
                  </span>
                )}
                <StatusBadge
                  caseId={c.id}
                  status={c.status}
                  onChanged={(newStatus) =>
                    setCases((prev) =>
                      prev.map((x) => (x.id === c.id ? { ...x, status: newStatus } : x)),
                    )
                  }
                />
              </div>
            );
          })
        )}
      </div>

      {nextCursor && (
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <button className="btn-cancel" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? t('common:actions.loading_more') : t('common:actions.load_more')}
          </button>
        </div>
      )}
    </div>
  );
}
